import { FormatMessageType } from '@hooks/useValidation';
import { LIST, MIN_FIELD, REQUIRED_FIELD } from '@utils/validation/validation.constant';
import Yup from '@utils/validation/yup-extended';

import { Mark, MarkEnum } from '@/types/commons/commons.types';
import { Zone2DTypeEnum } from '@/types/config/config.types';
import { ProcedureModel } from '@/types/config/procedure.types';

export function zoneOldSchema(formatMessage: FormatMessageType) {
  return Yup.object({
    zoneName: Yup.string().required(
      formatMessage({ id: REQUIRED_FIELD }, { field: formatMessage({ id: 'zone.name' }).toLowerCase() }),
    ),

    zoneType: Yup.mixed<Zone2DTypeEnum>()
      .oneOf(Object.values(Zone2DTypeEnum) as Zone2DTypeEnum[], formatMessage({ id: LIST }))
      .required(formatMessage({ id: REQUIRED_FIELD }, { field: formatMessage({ id: 'zone.type' }).toLowerCase() })),

    marks: Yup.array().when('zoneType', {
      is: (zoneType: Zone2DTypeEnum) =>
        [Zone2DTypeEnum.NFZ_INFO, Zone2DTypeEnum.NFZ_WARNING, Zone2DTypeEnum.NFZ_CRITICAL].includes(zoneType),
      then: (schema) =>
        schema.test(
          'platform-required',
          formatMessage({ id: REQUIRED_FIELD }, { field: formatMessage({ id: 'zone.platform' }).toLocaleLowerCase() }),
          (marks: Mark[] | undefined) => {
            return marks?.filter((mark) => mark.type === MarkEnum.PLATFORM).length === 1;
          },
        ),
    }),

    procedureModel: Yup.mixed<ProcedureModel>().when('zoneType', ([zoneType], schema) =>
      [Zone2DTypeEnum.NFZ_INFO, Zone2DTypeEnum.NFZ_WARNING, Zone2DTypeEnum.NFZ_CRITICAL].includes(zoneType)
        ? schema.required(
            formatMessage(
              { id: REQUIRED_FIELD },
              { field: formatMessage({ id: 'zone.procedureModel' }).toLowerCase() },
            ),
          )
        : schema.notRequired(),
    ),

    marginSize: Yup.number().when('zoneType', {
      is: (value: Zone2DTypeEnum) => value === Zone2DTypeEnum.IMZ,
      then: (schema) =>
        schema
          .required(
            formatMessage(
              { id: REQUIRED_FIELD },
              { field: formatMessage({ id: 'zone.marginSize' }).toLocaleLowerCase() },
            ),
          )
          .min(
            0,
            formatMessage(
              { id: MIN_FIELD },
              {
                value: 0,
                field: formatMessage({ id: 'zone.marginSize' }).toLocaleLowerCase(),
              },
            ),
          ),
    }),

    upperValue: Yup.number().when(['displayedFormat', 'lowerValue'], ([displayedFormat, lowerValue], schema) => {
      const minRequirement = !isNaN(lowerValue) ? lowerValue + 1 : -Infinity;
      return schema
        .min(
          minRequirement,
          formatMessage(
            { id: 'zone.upperError' },
            {
              field: formatMessage(
                { id: 'zone.upperField' },
                { field: formatMessage({ id: `zone.${displayedFormat}` }).toLowerCase() },
              ),
            },
          ),
        )
        .required(
          formatMessage(
            { id: REQUIRED_FIELD },
            {
              field: formatMessage(
                { id: 'zone.upperField' },
                { field: formatMessage({ id: `zone.${displayedFormat}` }).toLowerCase() },
              ),
            },
          ),
        );
    }),

    lowerValue: Yup.number().when(['displayedFormat'], ([displayedFormat], schema) => {
      return schema.required(
        formatMessage(
          { id: REQUIRED_FIELD },
          {
            field: formatMessage(
              { id: 'zone.lowerField' },
              { field: formatMessage({ id: `zone.${displayedFormat}` }).toLowerCase() },
            ),
          },
        ),
      );
    }),
  });
}
